import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Search,
  MessageCircle,
  Bell,
  HelpCircle,
  Settings,
  LogOut,
  ChevronDown,
  Plus,
  User,
  Calendar,
  Heart,
  ShieldCheck,
  House,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { isFocusedFlowPath } from '@/components/admin/ChromeFrame';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import MobileLanguageNav from '@/components/layout/MobileLanguageNav';
import BuildStamp from '@/components/layout/BuildStamp';
import NotificationBell from '@/components/notifications/NotificationBell';
import AnnouncementBar from '@/components/layout/AnnouncementBar';
import { useHeaderProfile } from '@/hooks/useHeaderProfile';
import { useVisualViewportOffset } from '@/lib/anchorScroll';
import { useIsDesktop } from '@/hooks/use-mobile';
import { useChowanieNaglowka } from '@/hooks/useChowanieNaglowka';
import { useSettingsLinkState } from '@/lib/settingsModal';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/useNotifications';
import { useFavorites } from '@/hooks/useFavorites';
import { isProductionGated, pokazujPaneleDev } from '@/config/env';
import logoUrl from '@/assets/flathero-logo.svg';
import {
  resolveNavState,
  getPrimaryNavItems,
  getMenuItems,
  type NavIcon,
  type NavItem,
  type NavState,
} from '@/components/layout/navItems';

const NAV_ICONS: Record<NavIcon, typeof Search> = {
  search: Search,
  messages: MessageCircle,
  bell: Bell,
  help: HelpCircle,
  calendar: Calendar,
  heart: Heart,
};

const NavIconEl = ({ icon, className }: { icon?: NavIcon; className?: string }) => {
  if (!icon) return null;
  const Icon = NAV_ICONS[icon];
  return <Icon className={className ?? 'h-4 w-4'} />;
};

/**
 * Licznik nad ikoną. Dwa warianty, bo to dwie różne wiadomości: nieprzeczytane czekają na
 * reakcję (czerwień), a liczba zapisanych ofert to tylko stan — czerwona kropka mówiłaby
 * „coś się stało", a nic się nie stało.
 */
const CountBadge = ({ count, wariant = 'pilne' }: { count: number; wariant?: 'pilne' | 'stan' }) =>
  count > 0 ? (
    <span
      className={cn(
        'absolute flex items-center justify-center rounded-full font-semibold leading-none',
        wariant === 'pilne'
          ? '-right-0.5 -top-0.5 h-4 min-w-4 px-1 text-[10px] bg-destructive text-destructive-foreground'
          // Mniejsza i wypchnięta dalej poza ikonę. Przy 16-pikselowej ikonie plakietka w
          // rozmiarze „pilnym" przykrywa ją niemal w całości — na pulpicie zamiast serca
          // widać zieloną kropkę z liczbą, czyli licznik zasłania to, co liczy. Zmierzone
          // na zrzucie. Wariantu „pilne" świadomie nie ruszam: ma tę samą wadę, ale to
          // plakietka nieprzeczytanych wiadomości i nie przerabiam jej przy okazji.
          : '-right-2 -top-1.5 h-3.5 min-w-3.5 px-1 text-[9px] bg-primary text-primary-foreground',
      )}
    >
      {count > 9 ? '9+' : count}
    </span>
  ) : null;

interface HeaderProps {
  /**
   * True when rendered inside ChromeFrame's funnel viewport shell: the shell
   * owns positioning and pan compensation, so the header must not do either.
   */
  inViewportShell?: boolean;
}

const Header = ({ inViewportShell = false }: HeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Skupiony przepływ: onboarding najemcy, rejestracja/logowanie ORAZ kreator i
  // przejęcie u właściciela (CLAIM-02). Nagłówek schodzi wtedy do języka i
  // wylogowania — co zabiera z ekranu Wiadomości, Powiadomienia i Kalendarz,
  // bezużyteczne dla konta bez opublikowanego ogłoszenia, a przede wszystkim
  // ADRES MIESZKANIA: krok 1 prosi właściciela, żeby podał go z pamięci, a
  // nagłówek wypisywał go nad formularzem razem z numerem domu.
  const focusedFlow = isFocusedFlowPath(location.pathname);
  // The funnel drops the marketing announcement bar (Maciek 2026-07-25; the
  // mockups' onboarding frames carry no banner) — a skupiony przepływ tym
  // bardziej, bo banner jest zaproszeniem do wyjścia z niego.
  const isAuthFunnel = focusedFlow;
  const [session, setSession] = useState<Session | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Controlled so menu links can close it explicitly: since the settings
  // overlay keeps the underlying page (and this header) mounted, an
  // uncontrolled popover would still be open when the overlay closes.
  const [menuOpen, setMenuOpen] = useState(false);
  // Mobile: opens settings as an overlay above the current page. Desktop: undefined.
  const settingsState = useSettingsLinkState();

  // Publish the REAL header height (nav + optional AnnouncementBar) as a CSS var
  // so any sticky-below-the-header section (e.g. the SERP filter bar + map) pins
  // at the right offset instead of a hard-coded 3.5rem. Resilient: add/remove or
  // resize the banner and everything below reflows automatically.
  const headerRef = useRef<HTMLElement>(null);
  /**
   * WYNIKI NA UKŁADZIE PULPITOWYM: nagłówek odjeżdża przy przewijaniu w dół i wraca w górę
   * (`useChowanieNaglowka`), a blok wyszukiwania przykleja się pod `--app-header-offset`.
   * Tylko tam — na pozostałych stronach i na telefonie nagłówek stoi jak stał.
   */
  const isDesktop = useIsDesktop();
  const chowajNaWynikach = !inViewportShell && isDesktop && location.pathname === '/search';
  const schowany = useChowanieNaglowka(chowajNaWynikach, headerRef);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const vv = window.visualViewport;
    const root = document.documentElement.style;
    const publish = () => {
      const h = el.offsetHeight;
      root.setProperty('--app-header-h', `${h}px`);
      /**
       * `--app-header-offset` = gdzie NAPRAWDĘ kończy się nagłówek. Dwie rzeczy przesuwają
       * go poza jego wysokość:
       *  - schowanie na wynikach (wtedy 0 — blok wyszukiwania staje przy samej górze);
       *  - kompensacja przesunięcia widoku Safari (`useVisualViewportOffset` przesuwa SAM
       *    nagłówek o `visualViewport.offsetTop`). Blok przyklejony pod `--app-header-h`
       *    zostawał wtedy na miejscu, a nagłówek (z-50) nachodził na jego górę — pole
       *    obszaru znikało pod menu, zostawał odcięty rząd pastylek (zrzut Piotra z iPada,
       *    28.09). Liczymy więc przesunięcie do offsetu, na układzie pulpitowym wyników;
       *    telefon ma własny układ i tego nie dotykamy.
       */
      const pan = chowajNaWynikach && vv && vv.offsetTop > 1 ? Math.round(vv.offsetTop) : 0;
      root.setProperty('--app-header-offset', `${schowany ? 0 : h + pan}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    vv?.addEventListener('resize', publish);
    vv?.addEventListener('scroll', publish);
    return () => {
      ro.disconnect();
      vv?.removeEventListener('resize', publish);
      vv?.removeEventListener('scroll', publish);
      root.removeProperty('--app-header-offset');
    };
  }, [schowany, chowajNaWynikach]);

  // iOS pans the visual viewport when the keyboard opens, which drags this
  // Pan compensation is for the NORMAL document layout only. Inside the funnel
  // viewport shell the whole shell is already translated by the same distance,
  // so compensating again here would double it (ChromeFrame explains the model).
  useVisualViewportOffset(inViewportShell ? { current: null } : headerRef);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => setSession(nextSession));
    return () => subscription.unsubscribe();
  }, []);

  const {
    role,
    displayName,
    fullName,
    avatarUrl,
    ownerAddress,
    ownerCompletionPct,
    ownerVerified,
    loading: profileLoading,
  } = useHeaderProfile(session);
  const accountEmail = session?.user?.email ?? null;
  const navState: NavState = resolveNavState(!!session, role);
  const { messageUnseenCount } = useNotifications();
  // Liczba zapisanych ofert — ten sam hook, co karta i strona /ulubione, więc licznik w
  // nagłówku nie może się rozjechać z listą.
  const { ids: ulubione } = useFavorites();
  const liczbaUlubionych = ulubione.length;

  // Render a nav icon, overlaying a count badge on Messages (unread) and on the heart
  // (saved offers). Liczniki siedzą TUTAJ, a nie w etykiecie, bo tę samą funkcję rysują
  // trzy miejsca — pasek na pulpicie, szybkie ikony na telefonie i szuflada — i doklejanie
  // „(3)" do napisu trzeba by było powtórzyć w każdym z nich oraz w czterech językach.
  const navIcon = (icon: NavIcon | undefined, className?: string) => {
    const el = <NavIconEl icon={icon} className={className} />;
    if (icon === 'messages' && messageUnseenCount > 0) {
      return (
        <span className="relative inline-flex">
          {el}
          <CountBadge count={messageUnseenCount} />
          <span className="sr-only">{t('messages.unread_aria', { count: messageUnseenCount })}</span>
        </span>
      );
    }
    if (icon === 'heart' && liczbaUlubionych > 0) {
      return (
        // `mr-1.5` robi miejsce na plakietkę, która wystaje 8 px poza ikonę. Bez tego
        // wchodziła 2 px na pierwszą literę etykiety — zmierzone, nie oszacowane.
        <span className="relative mr-1.5 inline-flex">
          {el}
          <CountBadge count={liczbaUlubionych} wariant="stan" />
          <span className="sr-only">{t('favorites.subtitle', { count: liczbaUlubionych })}</span>
        </span>
      );
    }
    return el;
  };

  const closeMobile = () => setMobileOpen(false);

  const handleLogout = async () => {
    closeMobile();
    await supabase.auth.signOut();
    navigate('/');
  };

  const gatedNoSession = isProductionGated() && !session;
  // Until the role has actually loaded, `navState` defaults to `tenant` — so
  // rendering the nav straight away flashes tenant links (Szukaj / Wiadomości /
  // Powiadomienia) at owners and agents. Hold the role-dependent
  // entries back for that first tick instead. Logged-out visitors never wait:
  // `loading` is only ever true when there IS a session.
  const primaryItems =
    focusedFlow || gatedNoSession || profileLoading
      ? []
      : getPrimaryNavItems(navState, { maUlubione: liczbaUlubionych > 0 });
  const menuItems = getMenuItems(navState);

  // Mobile top bar surfaces the quick-access messages + notifications icons
  // (next to the hamburger) for logged-in actors. Search/marketing links live
  // in the drawer only, so we keep just the messages + bell icons here.
  // Serce dolacza do czatu i dzwonka takze tutaj — te trzy to jeden rodzaj wejscia („moje
  // rzeczy"), a wyszukiwarka i linki marketingowe zostaja w szufladzie.
  const mobileQuickIcons = primaryItems.filter(
    (item) => item.icon === 'messages' || item.icon === 'bell' || item.icon === 'heart',
  );

  const avatar = (
    <Avatar className="h-8 w-8">
      {avatarUrl && <AvatarImage src={avatarUrl} alt="" />}
      <AvatarFallback className="text-xs">
        {displayName ? displayName.charAt(0).toUpperCase() : <User className="h-4 w-4" />}
      </AvatarFallback>
    </Avatar>
  );

  /* Hover callout with the logged-in user's account identity: full name on the
     first line, the email they're logged in with on the second. Desktop-only
     affordance (tooltips don't fire on touch); rendered when we have either. */
  const withNameTooltip = (node: ReactNode) =>
    fullName || accountEmail ? (
      <Tooltip>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent className="flex flex-col gap-0.5">
          {fullName && <span className="font-semibold leading-tight">{fullName}</span>}
          {accountEmail && (
            <span className="text-xs leading-tight text-muted-foreground break-all">{accountEmail}</span>
          )}
        </TooltipContent>
      </Tooltip>
    ) : (
      node
    );

  /* ----- shared secondary menu (the hamburger popover, logged-in states) ----- */
  const secondaryMenu = (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-accent"
          aria-label={t('header.open_menu')}
        >
          <Menu className="h-5 w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div className="py-1">
          {menuItems.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              // Settings opens as an overlay above the current page on mobile —
              // carry it as the background so closing lands back here (settingsModal.ts).
              state={item.key === 'settings' ? settingsState : undefined}
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent"
            >
              {item.key === 'settings' ? <Settings className="h-4 w-4" /> : <NavIconEl icon={item.icon} />}
              {t(`header.${item.key}`)}
            </Link>
          ))}
          <div className="border-t border-border" />
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent"
          >
            <LogOut className="h-4 w-4" />
            {t('header.logout')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );

  /* ----- owner flat entry: "Profil Mieszkania" links to the listing; the
     chevron opens the single-flat switcher where add-flat is disabled. ----- */
  const ownerFlatMenu = (
    <div className="flex items-center">
      {withNameTooltip(
        <Link
          to="/owner/listing"
          className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-foreground transition-colors hover:text-primary"
        >
          {avatar}
          <span className="hidden lg:flex lg:flex-col lg:leading-tight">
            <span className="flex items-center gap-1.5">
              {t('header.apartment_profile')}
              {/* Completeness nudge (Piotr 2026-07-15): pulls the owner back
                  into the console while gaps remain; hidden at 100%. */}
              {ownerCompletionPct != null && ownerCompletionPct < 100 && (
                <span
                  className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-primary"
                  title={t('my_apartment.completion_label')}
                >
                  {ownerCompletionPct}%
                </span>
              )}
              {/* Full-verification trust mark (Piotr 2026-07-22). */}
              {ownerVerified && (
                <ShieldCheck
                  className="h-3.5 w-3.5 text-success"
                  aria-label={t('search.card.owner_verified')}
                />
              )}
            </span>
            {ownerAddress && (
              <span className="text-xs font-normal text-muted-foreground">{ownerAddress}</span>
            )}
          </span>
        </Link>,
      )}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label={t('header.add_flat')}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1">
          <button
            type="button"
            disabled
            aria-disabled="true"
            className="flex w-full cursor-not-allowed items-center gap-2 rounded px-2 py-2 text-sm text-muted-foreground opacity-60"
          >
            <Plus className="h-4 w-4" />
            {t('header.add_flat')}
          </button>
          <p className="px-2 pb-1 pt-0.5 text-xs text-muted-foreground">{t('header.add_flat_unavailable')}</p>
        </PopoverContent>
      </Popover>
    </div>
  );

  const desktopPrimaryLink = (item: NavItem) =>
    item.icon === 'bell' ? (
      <NotificationBell key={item.key} label={t('header.notifications')} iconClassName="h-4 w-4" />
    ) : (
      <Link
        key={item.key}
        to={item.to}
        state={item.state}
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        {navIcon(item.icon)}
        {t(`header.${item.key}`)}
      </Link>
    );

  return (
    <header
      ref={headerRef}
      className={cn(
        'inset-x-0 z-50 border-b border-border bg-white',
        // In the shell the header is a plain flex child above the scroller —
        // it cannot be scrolled away, so it needs no stickiness.
        inViewportShell ? 'shrink-0' : 'sticky top-0',
        chowajNaWynikach && 'transition-[translate] duration-200 ease-out',
      )}
      // `translate`, nie `transform`: `transform` należy do kompensacji widoku Safari
      // (`useVisualViewportOffset` ustawia go inline) i jedno nadpisywałoby drugie.
      style={schowany ? { translate: '0 -100%' } : undefined}
      aria-hidden={schowany || undefined}
    >
      {navState === 'logged_out' && !gatedNoSession && !isAuthFunnel && <AnnouncementBar />}

      <div className="max-w-content mx-auto px-4 h-14 flex items-center justify-between">
        <div className="flex items-end">
          <Link
            to="/"
            aria-label={t('header.home_aria')}
            className="strefa-dotyku relative flex items-center"
            onClick={closeMobile}
          >
            <img src={logoUrl} alt="" className="h-[1.575rem] w-auto" />
          </Link>
          {/* Dev-only shortcut to the homepage preview at /home (Maciek 23.10). Shown only on the
              developer sites, so it can never reach stage or production. */}
          {session && pokazujPaneleDev() && (
            <Link
              to="/home"
              aria-label={t('header.dev_home_preview')}
              title={t('header.dev_home_preview')}
              onClick={closeMobile}
              className="ml-2 flex h-8 w-8 items-center justify-center self-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <House className="h-4 w-4" />
            </Link>
          )}
          <BuildStamp />
        </div>

        {/* ---------- Desktop: logged-out marketing links hug the logo ---------- */}
        {navState === 'logged_out' && !gatedNoSession && (
          <nav
            aria-label={t('header.main_navigation')}
            className="hidden md:flex items-center gap-6 ml-8 mr-auto"
          >
            {primaryItems.map(desktopPrimaryLink)}
          </nav>
        )}

        {/* ---------- Desktop right cluster ---------- */}
        <div className="hidden md:flex items-center gap-5">
          {navState !== 'logged_out' && primaryItems.map(desktopPrimaryLink)}

          {navState === 'logged_out' ? (
            <>
              <LanguageSwitcher showHint />
              {!isProductionGated() && (
                <>
                  <Button variant="ghost" asChild>
                    <Link to="/login">{t('header.login')}</Link>
                  </Button>
                  <Button variant="default" asChild>
                    <Link to="/signup">{t('header.signup')}</Link>
                  </Button>
                </>
              )}
            </>
          ) : focusedFlow ? (
            <>
              <LanguageSwitcher />
              {session && (
                <Button variant="ghost" onClick={handleLogout}>
                  {t('header.logout')}
                </Button>
              )}
            </>
          ) : profileLoading ? (
            /* Role not known yet — show only the role-independent hamburger so
               we never flash the wrong identity entry (see primaryItems). */
            secondaryMenu
          ) : navState === 'owner' ? (
            <>
              {ownerFlatMenu}
              {secondaryMenu}
            </>
          ) : navState === 'agent' ? (
            /* Agents have no profile, no flat and no dashboard — just the
               hamburger (Settings + logout). */
            secondaryMenu
          ) : (
            <>
              {withNameTooltip(
                <Link
                  to="/profile"
                  className="flex items-center gap-2 text-sm text-foreground transition-colors hover:text-primary"
                >
                  {avatar}
                  <span className="hidden lg:inline">{t('header.my_profile')}</span>
                </Link>,
              )}
              {secondaryMenu}
            </>
          )}
        </div>

        {/* ---------- Mobile right cluster: quick icons + hamburger ---------- */}
        {!gatedNoSession && (
          <div className="flex items-center gap-0.5 md:hidden">
            {mobileQuickIcons.map((item) =>
              item.icon === 'bell' ? (
                <NotificationBell key={item.key} />
              ) : (
                <Link
                  key={item.key}
                  to={item.to}
                  state={item.state}
                  aria-label={t(`header.${item.key}`)}
                  className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-accent"
                >
                  {navIcon(item.icon, 'h-5 w-5')}
                </Link>
              ),
            )}
            <button
              type="button"
              className="inline-flex items-center justify-center h-10 w-10 rounded-md text-foreground hover:bg-accent"
              aria-label={mobileOpen ? t('header.close_menu') : t('header.open_menu')}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        )}
      </div>

      {/* ---------- Mobile drawer ---------- */}
      {mobileOpen && !gatedNoSession && (
        <div className="md:hidden bg-white border-b border-border">
          <nav
            aria-label={t('header.mobile_navigation')}
            className="max-w-content mx-auto px-4 py-3 flex flex-col"
          >
            {/* Profile / flat entry */}
            {navState === 'tenant' && !focusedFlow && !profileLoading && (
              <MobileLink to="/profile" onClick={closeMobile}>
                {avatar}
                {t('header.my_profile')}
              </MobileLink>
            )}
            {navState === 'owner' && !focusedFlow && !profileLoading && (
              <>
                <Link
                  to="/owner/listing"
                  onClick={closeMobile}
                  className="flex items-center gap-2 px-1 py-2 text-sm text-foreground transition-colors hover:text-primary"
                >
                  {avatar}
                  <span className="flex flex-col leading-tight">
                    <span className="flex items-center gap-1.5">
                      {t('header.apartment_profile')}
                      {/* Same completeness nudge as desktop (Piotr: both
                          platforms, always). */}
                      {ownerCompletionPct != null && ownerCompletionPct < 100 && (
                        <span
                          className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-primary"
                          title={t('my_apartment.completion_label')}
                        >
                          {ownerCompletionPct}%
                        </span>
                      )}
                      {ownerVerified && (
                        <ShieldCheck
                          className="h-3.5 w-3.5 text-success"
                          aria-label={t('search.card.owner_verified')}
                        />
                      )}
                    </span>
                    {ownerAddress && (
                      <span className="text-xs text-muted-foreground">{ownerAddress}</span>
                    )}
                  </span>
                </Link>
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="flex cursor-not-allowed items-center gap-2 px-1 py-2 pl-10 text-sm text-muted-foreground opacity-60"
                >
                  <Plus className="h-4 w-4" />
                  {t('header.add_flat')}
                </button>
              </>
            )}

            {/* Primary items */}
            {primaryItems.map((item) => (
              <MobileLink
                key={item.key}
                to={item.icon === 'bell' ? '/notifications' : item.to}
                state={item.state}
                onClick={closeMobile}
              >
                {navIcon(item.icon)}
                {t(`header.${item.key}`)}
              </MobileLink>
            ))}

            {/* Secondary menu items (logged-in) */}
            {menuItems.map((item) => (
              <MobileLink
                key={item.key}
                to={item.to}
                // Settings opens as an overlay above the current page on mobile (settingsModal.ts).
                state={item.key === 'settings' ? settingsState : undefined}
                onClick={closeMobile}
              >
                {item.key === 'settings' ? <Settings className="h-4 w-4" /> : <NavIconEl icon={item.icon} />}
                {t(`header.${item.key}`)}
              </MobileLink>
            ))}

            {/* Language lives in the top nav only when logged out (or mid-
                onboarding, before Settings is reachable); otherwise it's under
                Settings. In the drawer it's an inline accordion, not a dropdown. */}
            {(navState === 'logged_out' || focusedFlow) && (
              <MobileLanguageNav showHint={navState === 'logged_out'} />
            )}

            {/* Auth controls */}
            {session ? (
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center gap-2 px-1 py-2 text-sm text-foreground transition-colors hover:text-primary"
              >
                <LogOut className="h-4 w-4" />
                {t('header.logout')}
              </button>
            ) : !isProductionGated() ? (
              <div className="flex flex-col gap-2">
                <Button variant="outline" asChild className="w-full">
                  <Link to="/login" onClick={closeMobile}>
                    {t('header.login')}
                  </Link>
                </Button>
                <Button variant="default" asChild className="w-full">
                  <Link to="/signup" onClick={closeMobile}>
                    {t('header.signup')}
                  </Link>
                </Button>
              </div>
            ) : null}
          </nav>
        </div>
      )}
    </header>
  );
};

const MobileLink = ({
  to,
  state,
  onClick,
  children,
}: {
  to: string;
  /** Router location state (e.g. the settings overlay's backgroundLocation). */
  state?: unknown;
  onClick: () => void;
  children: ReactNode;
}) => (
  <Link
    to={to}
    state={state}
    onClick={onClick}
    className="flex items-center gap-2 px-1 py-2 text-sm text-foreground transition-colors hover:text-primary"
  >
    {children}
  </Link>
);

export default Header;
